/**
 * StepperEditor
 *
 * Property editor for stepper control type
 * Configures stepper appearance, colors, sizing, and behavior
 *
 * SRP: Responsible ONLY for configuring stepper properties
 * Control Type: 'stepper'
 *
 * Properties Configured (7):
 * 1. stepperOrientation - Vertical or horizontal (vertical/horizontal)
 * 2. activeStepColor - Color for active step
 * 3. completedStepColor - Color for completed steps
 * 4. upcomingStepColor - Color for upcoming steps
 * 5. stepSize - Size of step circle (px)
 * 6. labelPosition - Where to show labels (bottom/right/none)
 * 7. connectorLineWidth - Thickness of connecting line (px)
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';

/**
 * Stepper configuration
 */
interface StepperConfig {
  stepperOrientation?: 'vertical' | 'horizontal';
  activeStepColor?: string;
  completedStepColor?: string;
  upcomingStepColor?: string;
  stepSize?: number;
  labelPosition?: 'bottom' | 'right' | 'none';
  connectorLineWidth?: number;
}

/**
 * StepperEditor Component
 * Renders property editors for all stepper configuration
 */
const StepperEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as StepperConfig) || {},
    [control.config],
  );

  const handleOrientationChange = useCallback(
    (value: string) => onConfigChange('stepperOrientation', value as 'vertical' | 'horizontal'),
    [onConfigChange],
  );

  const handleActiveStepColorChange = useCallback(
    (value: string) => onConfigChange('activeStepColor', value),
    [onConfigChange],
  );

  const handleCompletedStepColorChange = useCallback(
    (value: string) => onConfigChange('completedStepColor', value),
    [onConfigChange],
  );

  const handleUpcomingStepColorChange = useCallback(
    (value: string) => onConfigChange('upcomingStepColor', value),
    [onConfigChange],
  );

  const handleStepSizeChange = useCallback(
    (value: number) => onConfigChange('stepSize', value),
    [onConfigChange],
  );

  const handleLabelPositionChange = useCallback(
    (value: string) => onConfigChange('labelPosition', value as 'bottom' | 'right' | 'none'),
    [onConfigChange],
  );

  const handleConnectorLineWidthChange = useCallback(
    (value: number) => onConfigChange('connectorLineWidth', value),
    [onConfigChange],
  );

  const orientationOptions = useMemo(
    () => [
      { label: 'Horizontal', value: 'horizontal' },
      { label: 'Vertical', value: 'vertical' },
    ],
    [],
  );

  const labelPositionOptions = useMemo(
    () => [
      { label: 'Bottom', value: 'bottom' },
      { label: 'Right', value: 'right' },
      { label: 'None', value: 'none' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section stepper-editor">
      <h3 className="af-property-editor__section-title">Stepper Properties</h3>

      <SelectPropertyEditor
        label="Orientation"
        value={config.stepperOrientation || 'horizontal'}
        onChange={handleOrientationChange}
        options={orientationOptions}
        helpText="Layout direction of stepper"
      />

      <ColorPropertyEditor
        label="Active Step Color"
        value={config.activeStepColor || '#309B48'}
        onChange={handleActiveStepColorChange}
        helpText="Color for the current/active step"
      />

      <ColorPropertyEditor
        label="Completed Step Color"
        value={config.completedStepColor || '#309B48'}
        onChange={handleCompletedStepColorChange}
        helpText="Color for completed steps"
      />

      <ColorPropertyEditor
        label="Upcoming Step Color"
        value={config.upcomingStepColor || '#E0E0E0'}
        onChange={handleUpcomingStepColorChange}
        helpText="Color for steps not yet reached"
      />

      <NumberPropertyEditor
        label="Step Size (px)"
        value={config.stepSize || 40}
        onChange={handleStepSizeChange}
        min={20}
        max={80}
        step={4}
        helpText="Diameter of step circles"
      />

      <SelectPropertyEditor
        label="Label Position"
        value={config.labelPosition || 'bottom'}
        onChange={handleLabelPositionChange}
        options={labelPositionOptions}
        helpText="Position of step labels"
      />

      <NumberPropertyEditor
        label="Connector Line Width (px)"
        value={config.connectorLineWidth || 2}
        onChange={handleConnectorLineWidthChange}
        min={1}
        max={8}
        step={1}
        helpText="Thickness of lines connecting steps"
      />
    </div>
  );
};

StepperEditorComponent.displayName = 'StepperEditor';

/**
 * Stepper Property Editor Registration
 */
export const StepperEditor: PropertyEditor = {
  type: 'stepper',
  label: 'Stepper',
  description: 'Configure stepper appearance, colors, sizing, and behavior',
  version: '1.0.0',
  render: (props) => <StepperEditorComponent {...props} />,
};

export default StepperEditor;
